import { cloneDeep } from "lodash";
import { FilterOptions } from "../../../common/types/roots/FilterOptions";
import { Facade, TableDataAndJournalParams } from "../../../common/Facade";
import { FilterSettings } from "../../../common/types/roots/FilterSettings";
import { FilterSettingsForm } from "../../../common/types/roots/FilterSettingsForm";
import { Report127ActFilterSettingsForm } from "../settings-form/Report127ActFilterSettingsForm";
import { Report127ActFilterSettings } from "../settings/Report127ActFilterSettings";
import { Report127ActTableDataRequest } from "../data-request/Report127ActTableDataRequest";
import { Report127ActJournalRequest } from "../journal/Report127ActJournalRequest";
import { report127ActFilterOptions } from "../report-127-act";
import i18nService from "@/services/i18n";
import { Report127ActRow } from "../table-data/table-data-types";

export class Report127ActFacade implements Facade {
    getFilterOptions(): FilterOptions {
        return cloneDeep(report127ActFilterOptions)
    }

    settingsToForm(settings: FilterSettings, options: FilterOptions): FilterSettingsForm {
        // 1. Валидаций
        if (settings.reportName !== '1-27-ACT') {
            throw new Error('error_type_filter_settings')
        }
        if (options.key !== '1-27-ACT') {
            throw new Error('error_type_filter_options')
        }

        let reportName = options.reportNames.find(it => it.value === settings.reportName)
        if (!reportName) {
            throw new Error('error_not_found_report_name')
        }
        reportName = { ...reportName }

        let reportType = options.reportTypes.find(it => it.value === settings.reportType)!
        if (!reportType) {
            throw new Error('error_not_found_report_type')
        }
        reportType = { ...reportType }

        let classificationType = options.classificationTypes.find(it => it.value === settings.classificationType)
        if (!classificationType) {
            throw new Error('error_not_found_class_type')
        }
        classificationType = { ...classificationType }

        let dataSource = options.dataSources.find(it => it.value === settings.dataSource)
        if (!dataSource) {
            throw new Error('error_not_found_data_source')
        }
        dataSource = { ...dataSource }

        let periodicity = options.periodicities.find(it => it.value === settings.periodicity)
        if (!periodicity) {
            throw new Error('error_not_found_periodicity')
        }
        periodicity = { ...periodicity }

        let regionOption = options.regions.find(it => it.value === settings.region)
        if (!regionOption) {
            throw new Error('error_not_found_region_option')
        }
        regionOption = { ...regionOption }

        const budgetTypes = cloneDeep(
            options.budgetTypes.filter(it => settings.budgetTypes.includes(it.value))
        )

        const statuses = cloneDeep(
            options.statuses.filter(it => settings.statuses.includes(it.value))
        )

        let measureUnit = options.measureUnits.find(it => it.value === settings.measureUnit)
        if (!measureUnit) {
            throw new Error('error_not_found_measure_unit')
        }
        measureUnit = { ...measureUnit }

        let month = options.months.find(it => it.value == settings.month) ?? null
        if (month) {
            month = { ...month }
        }

        let isRoundUp: boolean = false
        if (settings.roundUpTo != null) {
            isRoundUp = true
        }
        let mutuallyRedeemingOption = options.mutuallyRedeeming.find(it => it.value === settings.mutuallyRedeeming)
        if (!mutuallyRedeemingOption) {
            throw new Error('error_not_found_mutually_redeeming_option')
        }
        mutuallyRedeemingOption = { ...mutuallyRedeemingOption }
        const mutuallyRedeemingLevelOptions = cloneDeep(
            options.mutuallyRedeemingLevels.filter(it => settings.mutuallyRedeemingLevels.includes(it.value))
        )
        // 2. Формирование
        const form: Report127ActFilterSettingsForm = {
            key: '1-27-ACT',

            reportName: reportName,
            reportType: reportType,
            classificationType: classificationType,
            dataSource: dataSource,
            periodicity: periodicity,

            region: regionOption,
            mutuallyRedeeming: mutuallyRedeemingOption,
            mutuallyRedeemingLevels: mutuallyRedeemingLevelOptions,
            budgetTypes: budgetTypes,
            statuses: statuses,
            measureUnit: measureUnit,
            year: settings.year,
            month: month,
            roundUp: isRoundUp,
            roundUpTo: settings.roundUpTo ?? null
        }

        return form
    }

    formToSettings(form: FilterSettingsForm): FilterSettings {
        if (form.key !== '1-27-ACT') {
            throw new Error('error_type_filter_settingsForm')
        }
        if (form.mutuallyRedeeming == null) {
            throw new Error('error_input_mutually_redeeming')
        }
        if (form.measureUnit == null) {
            throw new Error('error_input_measure_unit')
        }
        if (form.year == null) {
            throw new Error('error_input_year')
        }
        if (form.region == null) {
            throw new Error('error_input_region')
        }
        if (form.month == null) {
            throw new Error('error_input_month')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }

        const settings: Report127ActFilterSettings = {
            reportName: '1-27-ACT',

            reportType: form.reportType.value,
            classificationType: form.classificationType.value,
            dataSource: form.dataSource.value,
            periodicity: form.periodicity.value,

            region: form.region.value,
            mutuallyRedeeming: form.mutuallyRedeeming.value,
            mutuallyRedeemingLevels: form.mutuallyRedeemingLevels.map(it => it.value),
            budgetTypes: form.budgetTypes.map(it => it.value),
            statuses: form.statuses.map(it => it.value),
            measureUnit: form.measureUnit.value,
            year: form.year,
            month: form.month.value,
            roundUpTo: roundUpTo
        }

        return settings
    }

    getEmptyForm(): FilterSettingsForm {
        const options = cloneDeep(report127ActFilterOptions)

        const emptyForm: Report127ActFilterSettingsForm = {
            key: '1-27-ACT',

            reportName: options.reportNames.find(it => it.value === '1-27-ACT')!,
            reportType: options.reportTypes.find(it => it.value === 'REGULATED')!,
            classificationType: options.classificationTypes.find(it => it.value === 'MIXED')!,
            dataSource: options.dataSources.find(it => it.value === 'LOADER')!,
            periodicity: options.periodicities.find(it => it.value === 'MONTH')!,

            region: null,
            mutuallyRedeeming: options.mutuallyRedeeming.find(it => it.value === 'WITH')!,
            mutuallyRedeemingLevels: options.mutuallyRedeemingLevels,
            budgetTypes: [],
            statuses: options.statuses.filter(it => it.value === '39'),
            measureUnit: options.measureUnits.find(it => it.value === 'THOUSAND')!,
            year: (new Date().getFullYear()),
            month: null,
            roundUp: false,
            roundUpTo: null
        }

        return emptyForm
    }

    async constructReport(
        form: FilterSettingsForm,
        templateName: string,
        excelName: string | null,
        userId: string,
    ): Promise<TableDataAndJournalParams> {
        // 1. Валидаций
        if (form.key !== '1-27-ACT') {
            throw new Error('error_type_filter_settingsForm')
        }

        if (!form.year || !form.month) {
            throw new Error('error_input_year_month')
        }

        if (!form.region) {
            throw new Error('error_input_region')
        }

        if (form.measureUnit == null) {
            throw new Error('error_input_measure_unit')
        }
        if (form.mutuallyRedeeming == null) {
            throw new Error('error_input_mutually_redeeming')
        }
        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }
        const mutually_redeeming_levels = form.mutuallyRedeemingLevels.map(it => {
            if (it.value === 'TYPE-3') {
                return 3
            } else if (it.value === 'TYPE-4') {
                return 4
            } else {
                throw new Error('error_unsupported_vzk_level')
            }
        })
        // 2. Формирование данных для таблицы
        const params: Report127ActTableDataRequest = {
            year: form.year,
            month: form.month.value,
            status_list: form.statuses.map(it => Number(it.value)),
            region: form.region.value,
            mutually_redeeming: form.mutuallyRedeeming.value,
            mutually_redeeming_levels: mutually_redeeming_levels,
            budget_types: form.budgetTypes.map(it => it.value),
            measure_unit: form.measureUnit.value,
            round_up_to: roundUpTo
        }
        const response = await fetch('/api-py/monitoring/reports-constructor/act-1-27',
            {
                method: 'POST',
                body: JSON.stringify(params)
            }
        )
        if (!response.ok) {
            throw new Error('error_api_report_data_failed')
        }
        const responseTableData = await response.json()

        const tableData: Report127ActRow[] = transformIntoRows(responseTableData, roundUpTo)

        // 3. Формирование параметров для выгрузки журнал
        const settings: FilterSettings = this.formToSettings(cloneDeep(form))
        const journalParams: Report127ActJournalRequest = {
            reportName: "1-27-ACT",
            // journal data
            name: templateName,
            excel_name: excelName,
            filter_settings: settings,
            user_id: userId,
            // for excel
            params: params,
            locale: i18nService.locale
        }

        const result: TableDataAndJournalParams = {
            tableData: tableData,
            journalParams: journalParams
        }

        return result
    }
}

const transformIntoRows = (data: any, roundUpTo: number | null): Report127ActRow[] => {
    const rows: Report127ActRow[] = []

    const sectionKeys = [
        { name: 'general_income_total', localKey: 'act_general_income_total', departmentKey: 'general_income_total' },
        { name: 'general_expense_total', localKey: 'act_general_expense_total', departmentKey: 'general_expense_total' },
        { name: 'budget_loan_expense_total', localKey: 'act_budget_loan_expense_total', departmentKey: 'budget_loan_expense_total' },
        { name: 'budget_loan_income_total', localKey: 'act_budget_loan_income_total', departmentKey: 'budget_loan_income_total' },
        { name: 'financial_actives_expense_total', localKey: 'act_financial_actives_expense_total', departmentKey: 'financial_actives_expense_total' },
        { name: 'financial_actives_income_total', localKey: 'act_financial_actives_income_total', departmentKey: 'financial_actives_income_total' },
        { name: 'finance_deficit_budget_income_7_total', localKey: 'act_finance_deficit_budget_income_7_total', departmentKey: 'finance_deficit_budget_income_7_total' },
        { name: 'finance_deficit_budget_expense_total', localKey: 'act_finance_deficit_budget_expense_total', departmentKey: 'finance_deficit_budget_expense_total' },
    ]

    let outerIndex = 1
    data.forEach((sectionData: any) => {
        if (!['acc_sld_beg', 'acc_sld_end'].includes(sectionData.data_key)) {
            rows.push({
                number: String(outerIndex),
                name: sectionData.data_key,
                department: null,
                local: null,
                difference: null
            })
            sectionKeys
                .filter(it => {
                    if (sectionData.data_key === 'plgo') {
                        return [
                            'general_expense_total',
                            'budget_loan_expense_total',
                            'financial_actives_expense_total',
                            'finance_deficit_budget_expense_total'
                        ].includes(it.name)
                    } else {
                        return true
                    }
                }).forEach((keys, index) => {
                    const dif_val: number = sectionData[keys.localKey] - sectionData[keys.departmentKey]
                    rows.push({
                        number: `${outerIndex}.${index + 1}`,
                        name: keys.name,
                        local: sectionData[keys.localKey],
                        department: sectionData[keys.departmentKey],
                        difference: roundUpTo !== null && roundUpTo > 0 ? parseFloat(dif_val.toFixed(roundUpTo)) : dif_val
                    })
                })
        } else {
            const dif_val = sectionData.act_value - sectionData.value
            rows.push({
                number: String(outerIndex),
                name: sectionData.data_key,
                department: sectionData.act_value,
                local: sectionData.value,
                difference: roundUpTo !== null && roundUpTo > 0 ? parseFloat(dif_val.toFixed(roundUpTo)) : dif_val
            })
        }

        outerIndex++
    })

    rows.push({
        number: String(outerIndex),
        name: 'free_budget',
        department: null,
        local: null,
        difference: null
    })

    return rows
}
